import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import api, { formatApiError, setAuthToken, clearAuthToken, getAuthToken } from "../lib/api";

const AuthContext = createContext(null);

// Persisted user cache key — lets us re-hydrate the previous user
// object immediately on page load so the Header doesn't flash
// "Sign In" while /auth/me is in flight. The cache is just a
// convenience; /auth/me is still authoritative and overwrites
// this on success (or clears it on 401).
const USER_CACHE_KEY = "hw_user_cache";

function readCachedUser() {
  try {
    const raw = window.localStorage.getItem(USER_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeCachedUser(u) {
  try {
    if (u && typeof u === "object") {
      window.localStorage.setItem(USER_CACHE_KEY, JSON.stringify(u));
    } else {
      window.localStorage.removeItem(USER_CACHE_KEY);
    }
  } catch {}
}

export function AuthProvider({ children }) {
  // user === null  → still checking (we either have a token to validate,
  //                  or we're on the very first render)
  // user === false → confirmed logged out
  // user === obj   → confirmed logged in
  //
  // Why this matters: when the user pulls-to-refresh the home page on
  // mobile (or any reload), there's a brief gap between mount and the
  // /auth/me response. If we initialised user as `false`, the UI
  // would flash the logged-out home for a second, looking exactly
  // like the user got signed out. Initialising to the LAST-KNOWN
  // user object (from localStorage cache) keeps the UI in the
  // signed-in state during the rehydrate — /auth/me confirms or
  // corrects it asynchronously.
  const [user, setUser] = useState(() => {
    if (typeof window === "undefined") return null;
    // Only trust the cached user if we still hold an auth token.
    // (Token in localStorage AND user cache present = optimistic
    // signed-in state; everything else falls back to loading null.)
    try {
      if (window.localStorage.getItem("hw_auth_token")) {
        const cached = readCachedUser();
        if (cached) return cached;
      }
    } catch {}
    return null;
  });
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      writeCachedUser(data);
      // Auto-redeem ?ref=CODE referral — fires only when there's a
      // pending code in localStorage (captured on app boot before
      // the user was signed in). Idempotent on the backend; if the
      // user already redeemed a code we just clear the pending one
      // and move on. Grants 7 days premium to invitee + 30 days to
      // inviter on first successful redemption.
      try {
        const pending = window.localStorage.getItem("hw_pending_ref");
        if (pending) {
          api
            .post("/invite/redeem", { code: pending })
            .then(() => {
              try { window.localStorage.removeItem("hw_pending_ref"); } catch (_) {}
              // Soft refresh of `user` so the new premium-until shows
              // up everywhere (crown badge, paywall hide, etc.).
              api.get("/auth/me").then(({ data: fresh }) => {
                setUser(fresh);
                writeCachedUser(fresh);
              }).catch(() => {});
            })
            .catch(() => {
              // Either already redeemed or invalid — clear it so we
              // don't keep retrying forever.
              try { window.localStorage.removeItem("hw_pending_ref"); } catch (_) {}
            });
        }
      } catch (_) {}
      // Auto-capture browser timezone and persist to the user record
      // so the daily email scheduler can deliver Pure Verse / Reader's
      // Digest / Midday Verse / Lord's Prayer at the right LOCAL hour
      // for every subscriber on earth — not just at Pacific time.
      // Fires once per page-load; idempotent on the backend (skips
      // the write if the timezone hasn't changed).
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (tz && tz !== data?.timezone) {
          api.post("/me/timezone", { timezone: tz }).catch(() => {});
        }
      } catch { /* old browsers without Intl support — skip */ }
    } catch (e) {
      // Only treat a real auth failure (401/403) as "logged out".
      // Network blips (offline, DNS hiccup, server timeout) on mobile
      // pull-to-refresh used to set user=false and clear the token,
      // forcing a re-login the next time the network came back.
      // Now we keep the cached optimistic user in place on transient
      // errors and only sign out on definitive auth rejections.
      const status = e?.response?.status;
      if (status === 401 || status === 403) {
        setUser(false);
        writeCachedUser(null);
        try { clearAuthToken(); } catch (_) {}
      }
      // else: leave user as-is (cached optimistic state) and try
      // again on next load.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: skip /me check if returning from Google OAuth — AuthCallback will handle it
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    // No token? Short-circuit straight to logged-out so we don't fire
    // an unnecessary /auth/me request and so any sign-in CTAs render
    // immediately.
    if (!getAuthToken()) {
      setUser(false);
      writeCachedUser(null);
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data?.token) setAuthToken(data.token);
    setUser(data);
    writeCachedUser(data);
    try { window.localStorage.setItem("hw_preferred_signin", "email"); } catch {}
    return data;
  };

  const register = async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    if (data?.token) setAuthToken(data.token);
    setUser(data);
    writeCachedUser(data);
    try { window.localStorage.setItem("hw_preferred_signin", "email"); } catch {}
    return data;
  };

  const loginDemo = async () => {
    const { data } = await api.post("/auth/demo-login");
    if (data?.token) setAuthToken(data.token);
    setUser(data);
    writeCachedUser(data);
    return data;
  };

  const loginWithGoogle = () => {
    // Remember that this user prefers Google BEFORE the redirect — the
    // AuthCallback page is where the user object lands, but it doesn't
    // know the chosen flow. Persisting here means the Login page can
    // surface "Continue with Google" first the next time they need to
    // sign in (sessions can expire, devices can be cleared, etc.).
    try { window.localStorage.setItem("hw_preferred_signin", "google"); } catch {}
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    clearAuthToken();
    writeCachedUser(null);
    // INTENTIONAL: keep `hw_preferred_signin` across logout. Logging
    // out is the moment we MOST want to remember the user's method —
    // when they sign back in, the Login page surfaces their last
    // method first so they're one tap away.
    setUser(false);
  };

  // Expose a wrapper that keeps the cache in sync whenever any caller
  // (e.g. AuthCallback after Google sign-in) sets the user directly.
  const setUserCached = useCallback((u) => {
    setUser(u);
    if (u && typeof u === "object") writeCachedUser(u);
    else if (u === false) writeCachedUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser: setUserCached, loading, login, register, loginDemo, loginWithGoogle, logout, formatApiError }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
